'use client';

import * as React from 'react';
import type { ComponentType, SVGProps } from 'react';

import { cn } from '../lib/utils';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Lightweight section sub-header used inside a PageHeader'd page.
 * Pattern: optional icon + eyebrow-style label on the left, right-aligned
 * meta slot (e.g. "N entries", a count, a small action).
 *
 *   <SectionHeader icon={GitBranch} label="Managed repos" meta={`${n} entries`} />
 *   <SectionHeader label={`Repos (${n})`} />
 *
 * Typography + spacing are shared across all apps. Pass `className` to
 * override the default bottom margin (default `mb-3`).
 */

interface SectionHeaderProps {
  /** Optional leading icon. */
  icon?: IconType;
  /** Left label — rendered with the `eyebrow` typography class. */
  label: React.ReactNode;
  /** Right-aligned meta slot (count, small action). */
  meta?: React.ReactNode;
  className?: string;
}

export function SectionHeader({
  icon: Icon,
  label,
  meta,
  className,
}: SectionHeaderProps) {
  return (
    <div
      className={cn('mb-3 flex items-baseline justify-between', className)}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="size-4 opacity-60" />}
        <h3 className="eyebrow opacity-60">{label}</h3>
      </div>
      {meta !== undefined && meta !== null && (
        <span className="eyebrow opacity-40">{meta}</span>
      )}
    </div>
  );
}

export type { SectionHeaderProps };
